// Sprint-009 B4 — calendar copy (design-phase-b.md §2.8/§2.9/§2.10a/§8).
// Every string here is checked against `design-spec-today-card.md` §4's
// banned list (manager, role, permission, assignment, cadence, parent/
// child, delegate/delegation, token, session, magic link) plus the
// Amendment-5-specific additions this spec adds (occurrence, exception,
// suppressed, moved as raw technical nouns, schedule_exceptions) — see
// §8's own zero-jargon sweep table, which this file implements verbatim.
// The server renders every cadence PHRASE (`occurrence.phrase`) — this
// file only builds the SENTENCES around that phrase, never re-derives the
// phrase itself (same standing rule `types.ts`'s header comment states).

import { isSameDay, parseISODate, weekdayFullLabel } from "./calendarDates";
import { monthDayLabel } from "./recordsCopy";

// ---------------------------------------------------------------------------
// §2.8 — reschedule action-sheet copy
// ---------------------------------------------------------------------------

export function rescheduleSheetTitle(title: string): string {
  return `Move "${title}"?`;
}

/** R-B0b-1 (UX-required, Major) — the exact `{phrase}`/`{date}` template
 * this design-validation finding mandated, applied identically for every
 * cadence phrase (not just "every day" — see §2.8's own worked "every
 * week" example). */
export function rescheduleReassuranceCopy(phrase: string, dateLabel: string): string {
  return `It happens ${phrase}. Moving or skipping this one only changes ${dateLabel} — it'll still happen ${phrase} after that.`;
}

export const RESCHEDULE_ALREADY_DONE_ERROR = "That's already done — nothing to move.";

/** §2.9 — undo's own 409 (the linked one-off was itself already
 * completed). Deliberately split from its "Open it…" action label (passed
 * separately to `Snackbar`'s `actionLabel`) rather than folded into one
 * long sentence — see `hooks/useSchedule.ts`'s `undoException`. */
export const UNDO_ALREADY_DONE_MESSAGE = "That task was already done — undo isn't possible.";
export const UNDO_ALREADY_DONE_ACTION_LABEL = "Open it to see what happened";

export const CALENDAR_GENERIC_ERROR = "Couldn't do that — try again.";

/** §2.8's `moveTargetLabel(date, referenceRange)` rule: weekday name only
 * when the new date falls within the reference week (matching the
 * existing overdue-phrase convention, "day name only"); otherwise the
 * existing `monthDayLabel` helper ("August 3") — no new date-formatting
 * convention invented. `referenceWeekDays` is the 7 dates of "this week"
 * (the week containing today) — the one stable, always-meaningful anchor
 * regardless of which month/week the grid is currently showing (see
 * `hooks/useSchedule.ts`'s own docstring on why "today's week," not "the
 * currently-scrolled-to week," is the correct anchor here). */
export function moveTargetLabel(newDateIso: string, referenceWeekDays: Date[]): string {
  const newDate = parseISODate(newDateIso);
  const inRange = referenceWeekDays.some((d) => isSameDay(d, newDate));
  return inRange ? weekdayFullLabel(newDate) : monthDayLabel(newDateIso);
}

// ---------------------------------------------------------------------------
// §2.9 — Snackbar messages
// ---------------------------------------------------------------------------

export function skippedSnackbarMessage(title: string, dateLabel: string): string {
  return `Skipped '${title}' for ${dateLabel}.`;
}

export function movedSnackbarMessage(title: string, targetLabel: string): string {
  return `Moved '${title}' to ${targetLabel}.`;
}

export function addedSnackbarMessage(title: string, dateLabel: string): string {
  return `Added '${title}' for ${dateLabel}.`;
}

// ---------------------------------------------------------------------------
// §2.5 — chip labels (skip/moved ghost states)
// ---------------------------------------------------------------------------

export function skippedChipLabel(title: string): string {
  return `${title} — skipped`;
}

export function movedChipLabel(title: string, targetLabel: string): string {
  return `${title} — moved to ${targetLabel}`;
}

// ---------------------------------------------------------------------------
// §2.10a (R-B0b-3) — the member "ask someone" read-only hint.
//
// **Flagged deviation** (see PR body): `ScheduleOccurrenceOut`
// (`app/schemas/schedule.py`) carries no `assigned_by_name`-equivalent
// field a member's client could resolve dynamically — the spec's own
// fix text anticipated this ("`<name>` defaults to the admin's own display
// name unless the occurrence's own `assigned_by_name`... is more
// specific") and explicitly permits the literal default as the fallback.
// Since the field doesn't exist on this response at all, the literal
// default is what ships. A future multi-admin farm would need a real
// name field added to `ScheduleOccurrenceOut` to do better than this.
// ---------------------------------------------------------------------------

const FARM_ADMIN_DISPLAY_NAME = "your farm admin";

export function askToMoveHint(): string {
  return `Ask ${FARM_ADMIN_DISPLAY_NAME} to move this.`;
}

// ---------------------------------------------------------------------------
// §2.10 — completion-wins read-only detail popover.
//
// **Flagged deviation**: the mock shows "Done — marked by Worker One on July
// 14," but `ScheduleOccurrenceOut` carries no completed-by attribution
// (that lives only in the `completions` ledger, never joined into the
// schedule projection). Rather than fabricate an unverified name, this
// build shows the plain completed-date sentence only — see the PR body's
// deviations list for the same SA follow-up flag as the hint above.
// ---------------------------------------------------------------------------

export function doneDetailBody(dateLabel: string): string {
  return `Done on ${dateLabel}.`;
}

// ---------------------------------------------------------------------------
// §2.7 — create one-off sheet copy (reuses `AddHealthNoteSheet`'s chrome
// and `HandOutWorkFlow`'s own step-1 heading verbatim, per §2.7's mandate).
// ---------------------------------------------------------------------------

export function createSheetTitle(dateLabel: string): string {
  return `Add a one-time task for ${dateLabel}`;
}

export const CREATE_SHEET_WHAT_LABEL = "What needs doing?";
export const CREATE_SHEET_WHO_LABEL = "Who does it (optional)";
export const CREATE_SHEET_SUBMIT_LABEL = "Add it";
